import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "@store/store";
import {
	dismiss as dismissAction,
	resetNotifications,
	setNotifications,
	markRead as markReadAction,
	updateUnreadCount,
} from "@store/slices/notificationSlice";
import { fetchNotificationsService, fetchUnreadCountService } from "./services/fetchNotificationsService";
import { markNotificationReadService } from "./services/markNotificationRead";

export default function useNotifications() {
	const dispatch = useDispatch<AppDispatch>();
	const notifications = useSelector((state: RootState) => state.notifications.notifications);
	const loading = useSelector((state: RootState) => state.notifications.loading);
	const hasMore = useSelector((state: RootState) => state.notifications.hasMore);
	const page = useSelector((state: RootState) => state.notifications.page);
	const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);

	// Fetch notifications and update state
	const fetchInitial = useCallback(async () => {
		dispatch(resetNotifications());
		try {
			const data = await fetchNotificationsService(1);
			if (data) {
				dispatch(
					setNotifications({
						notifications: data?.data,
						page: data?.pagination?.page,
						hasMore: data?.pagination?.hasNextPage,
						unreadCount: data?.unreadCount,
					}),
				);
			}
		} catch (err) {
			// handle error, e.g. show notification
			console.error("Error fetching notifications =>", err);
		}
	}, [dispatch]);

	const loadMore = useCallback(async () => {
		if (hasMore && !loading) {
			try {
				const data = await fetchNotificationsService(page + 1);
				if (data && Array.isArray(data?.data)) {
					dispatch(
						setNotifications({
							notifications: [...notifications, ...data.data],
							page: data?.pagination?.page,
							hasMore: data?.pagination?.hasNextPage,
							unreadCount: data?.unreadCount,
						}),
					);
				}
			} catch (err) {
				// handle error
				console.error("Error loading more notifications =>", err);
			}
		}
	}, [dispatch, hasMore, loading, page, notifications]);

	const markRead = async (id: string) => {
		try {
			// First, optimistically update the UI
			dispatch(markReadAction(id));

			// Then make the API call
			await markNotificationReadService(id);

			// Finally, refresh the unread count from server in background
			// This ensures the unread count is accurate without affecting the current list
			const updatedUnreadCount = await fetchUnreadCountService();
			if (updatedUnreadCount !== null) {
				dispatch(updateUnreadCount(updatedUnreadCount));
			}
		} catch (err) {
			console.error("Error marking notification as read =>", err);
			// On error, we could revert the optimistic update, but for now we'll leave it
			// since the user already sees the notification as read
		}
	};

	const dismiss = (id: string) => {
		dispatch(dismissAction(id));
	};

	return {
		notifications,
		loading,
		hasMore,
		fetchInitial,
		unreadCount,
		loadMore,
		markRead,
		dismiss,
	};
}
